import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { pickedDateStr } from '@app/shared/time';

// Les trois états d'une quantité commandée : BL réalisé, BL créé non réalisé, reste qu'aucun BL ne porte.
export type TrackingKind = 'delivered' | 'planned' | 'remaining';
export const TRACKING_KINDS: TrackingKind[] = ['delivered', 'planned', 'remaining'];

// Quantités de la ligne de commande (tous ses bons) ; `qty` = ce que porte CETTE pièce : la ligne du BL, ou le reste.
export interface TrackingLine {
	id_order_l: number;
	seq: number;
	typ: string;
	product: number;
	ppv: number | null;
	product_name: string | null;
	terms_name?: string | null;	// intitulé du contrat de location de la ligne louée ; null pour une vente
	packaging_name: string | null;
	qtybaseuom: number | null;
	barcode: string | null;
	ordered: number;
	delivered: number;
	planned: number;
	canceled: number;
	remaining: number;
	qty: number;
}

// Une pièce de GET /deliveries/tracking : un BL (livré ou programmé) ou le reste à livrer d'une commande (delivery_id nul).
export interface TrackingPiece {
	kind: TrackingKind;
	delivery_id: number | null;
	bl_index: number | null;			// rang du BL dans la fenêtre de livraison (?index=)
	order_id: number;
	doc_type: string;				// FACTURE (F2) ou LOCATION (dossier, avec « Location » seulement)
	order_state: string;
	created: string | null;
	validated: string | null;			// dates.preparation brut
	validated_day: string | null;		// jour de validation à Paris, celui des filtres Du/Au
	customer_id: number;
	customer_name: string;
	customer_city: string | null;
	deliv_name: string;
	deliv_street: string | null;
	deliv_city: string;
	station: number | null;
	store: number | null;
	id_user: number | null;
	mode: number | null;
	askdatdel: string | null;
	askslot: number | null;
	plandatdel: string | null;
	planslot: number | null;
	realdatdel: string | null;
	datinvoicing: string | null;
	id_del_run: number | null;
	has_proof: boolean;				// scan du BL papier ou BL signé à l'écran
	passage: number | null;			// BL du dossier de location : le BL lui-même, ou celui avec lequel ce BL de vente est livré
	passage_order: number | null;	// le dossier de location de ce passage
	lines: TrackingLine[];
}

// Critères lus par le serveur ; magasin et employé se filtrent dans le navigateur.
export interface TrackingQuery {
	kinds: TrackingKind[];
	from?: Date;
	to?: Date;
	customer?: number;
	product?: number;
	canceled: boolean;
	order?: number;					// une seule commande, tous états confondus
	rental: boolean;				// BL des dossiers de location en plus (livrés et programmés, jamais de reste)
}

@Injectable({ providedIn: 'root' })
export class DeliveryTrackingService {

	private http = inject(HttpClient);

	getPieces$(q: TrackingQuery): Observable<TrackingPiece[]> {
		let params = new HttpParams().set('kinds', q.kinds.join(','));
		const from = pickedDateStr(q.from), to = pickedDateStr(q.to);
		if (from) params = params.set('from', from);
		if (to) params = params.set('to', to);
		if (q.customer) params = params.set('customer', q.customer);
		if (q.product) params = params.set('product', q.product);
		if (q.canceled) params = params.set('canceled', 'true');
		if (q.order) params = params.set('order', q.order);
		if (q.rental) params = params.set('rental', 'true');
		return this.http.get<TrackingPiece[]>(environment.server_url + '/deliveries/tracking', { params });
	}
}
